import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Toast } from './Toast';
import {
  DEFAULT_TOAST_DURATION_MS,
  ToastContext,
  type ToastApi,
  type ToastOptions,
} from './toastContext';

interface ActiveToast extends ToastOptions {
  id: number;
}

/** Mount once near the app root; any descendant can call `useToast()`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const nextId = useRef(1);
  const activeId = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const dismissToast = useCallback(
    (id?: number) => {
      if (id !== undefined && id !== activeId.current) {
        return;
      }
      clearTimer();
      activeId.current = null;
      setToast(null);
    },
    [clearTimer]
  );

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      clearTimer();
      activeId.current = id;
      setToast({ ...options, id });
      timer.current = setTimeout(
        () => dismissToast(id),
        options.durationMs ?? DEFAULT_TOAST_DURATION_MS
      );
      return id;
    },
    [clearTimer, dismissToast]
  );

  useEffect(() => clearTimer, [clearTimer]);

  const api = useMemo<ToastApi>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        data-testid="toast-region"
        data-placement={toast?.placement ?? 'top'}
        className={`pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-4 ${
          toast?.placement === 'bottom'
            ? 'bottom-[calc(6rem+env(safe-area-inset-bottom,0px))]'
            : 'top-[max(1rem,env(safe-area-inset-top))]'
        }`}
      >
        {toast && (
          <Toast
            key={toast.id}
            message={toast.message}
            actionLabel={toast.actionLabel}
            onAction={() => {
              toast.onAction?.();
              dismissToast(toast.id);
            }}
            onDismiss={() => dismissToast(toast.id)}
          />
        )}
      </div>
    </ToastContext.Provider>
  );
}
